import React, { Component } from "react";
import { Redirect } from "react-router-dom";
import client, { signUpload } from "@culturehq/client";
import { EmailField, Form, ImageField, SelectField, StringField, SubmitButton, DateTimeField } from "@culturehq/components";

import { useProfile, useUserSelectOptions } from "app/context";
import { useFieldsLabels } from "app/hooks";
import { canViewCompaniesField } from "app/permissions";
import { t, Trans } from "app/utils";

import ErrorMessage from "components/common/ErrorMessage";

const getInitialValues = (
  { avatar, location, companies, departments, classYear, programs, degree, ...initialValues }
) => ({
  avatar: avatar.originalUrl,
  locationName: location?.name,
  companyNames: companies.map(({ name }) => name),
  departmentNames: departments.map(({ name }) => name),
  classYearName: classYear?.name,
  degreeName: degree?.name,
  programNames: programs?.map(({ name }) => name),
  ...initialValues
});

class ProfileEditContext extends Component {
  state = { error: false, imageProgress: null, redirectTo: false };

  handleSubmit = async ({ avatar, ...others }) => {
    const { fetchedProfile } = this.props;

    this.setState({ error: false });

    try {
      const values = others;
      if (avatar && avatar.size) {
        values.avatarUrl = await signUpload(avatar, imageProgress => {
          this.setState({ imageProgress });
        });
      }

      const { user } = await client.updateProfile(values);
      fetchedProfile(user);

      this.setState({ redirectTo: "/profile" });
    } catch (error) {
      if (error.status === 422) {
        this.setState({ error });
      } else {
        throw error;
      }
    }
  };

  render() {
    const {
      profile,
      classYearOptions,
      companyOptions,
      departmentOptions,
      labels,
      locationOptions,
      programOptions,
      degreeOptions
    } = this.props;
    const { organization } = profile;
    const { error, imageProgress, redirectTo } = this.state;
    const offset = -new Date().getTimezoneOffset();

    if (redirectTo) {
      return <Redirect to={redirectTo} />;
    }

    return (
      <div className="Edit-Profile">
        <Form onSubmit={this.handleSubmit} initialValues={getInitialValues(profile)}>
          {error && <ErrorMessage error={error} />}
          <ImageField name="avatar" aspectRatio={1} progress={imageProgress}>
            <Trans>Profile Image</Trans>
          </ImageField>
          {organization.firstNameLastNameEnabled ? (
            <>
              <StringField name="firstName" required>
                <Trans>First Name</Trans>
              </StringField>
              <StringField name="lastName" required>
                <Trans>Last Name</Trans>
              </StringField>
            </>
          ) : (
            <StringField name="name" required>
              <Trans>Name</Trans>
            </StringField>
          )}
          {organization.passwordsEnabled && (
            <EmailField name="email" required>
              <Trans>Email Address</Trans>
            </EmailField>
          )}
          {!organization.isEducational && (
            <StringField name="title">
              <Trans>Title</Trans>
            </StringField>
          )}
          {organization.isEducational && (
            <StringField name="biography" max="80">
              <Trans>Biography</Trans>
            </StringField>
          )}
          <SelectField name="locationName" crecpatable required options={locationOptions}>
            <Trans>Location</Trans>
          </SelectField>
          {!organization.isEducational && (
            <SelectField name="departmentNames" creatable multiple options={departmentOptions}>
              <Trans>{labels.departments.multipleField}</Trans>
            </SelectField>
          )}
          {canViewCompaniesField(organization) && (
            <SelectField name="companyNames" creatable multiple options={companyOptions}>
              <Trans>{labels.companies.multipleField}</Trans>
            </SelectField>
          )}
          {!organization.isEducational && (
            <DateTimeField name="startedOn" offset={offset}>
              <Trans>Start date at the company</Trans>
            </DateTimeField>
          )}
          {organization.isEducational && (
            <>
              <SelectField name="degreeName" creatable options={degreeOptions}>
                <Trans>Degree</Trans>
              </SelectField>
              <SelectField name="programNames" creatable multiple options={programOptions}>
                <Trans>Programs</Trans>
              </SelectField>
              <SelectField name="classYearName" creatable options={classYearOptions}>
                <Trans>{organization.name === "Dartmouth Guarini" ? "Years in Program" : "Class year"}</Trans>
              </SelectField>
            </>
          )}
          <SubmitButton primary>
            {submitting => (submitting ? t("Submitting...") : t("Save Changes"))}
          </SubmitButton>
        </Form>
      </div>
    );
  }
}

const ProfileEdit = () => {
  const { profile, fetchedProfile } = useProfile();
  const {
    classYearOptions,
    companyOptions,
    departmentOptions,
    locationOptions,
    programOptions,
    degreeOptions
  } = useUserSelectOptions();
  const labels = useFieldsLabels();

  return (
    <ProfileEditContext
      profile={profile}
      fetchedProfile={fetchedProfile}
      classYearOptions={classYearOptions}
      companyOptions={companyOptions}
      departmentOptions={departmentOptions}
      labels={labels}
      locationOptions={locationOptions}
      programOptions={programOptions}
      degreeOptions={degreeOptions}
    />
  );
};

export default ProfileEdit;
